/** Province labels aligned with onboarding `PROVINCE_OPTIONS`. */
export const PAKISTAN_PROVINCES = [
  'Islamabad Capital Territory',
  'Punjab',
  'Sindh',
  'Khyber Pakhtunkhwa',
  'Balochistan',
] as const;

export type PakistanProvince = (typeof PAKISTAN_PROVINCES)[number];

export const CITIES_BY_PROVINCE: Record<PakistanProvince, readonly string[]> = {
  'Islamabad Capital Territory': [
    'Islamabad',
    'Wah Cantonment',
  ],
  Punjab: [
    'Attock',
    'Bahawalnagar',
    'Bahawalpur',
    'Bhakkar',
    'Bhimber',
    'Chakwal',
    'Chiniot',
    'Dera Ghazi Khan',
    'Faisalabad',
    'Gojra',
    'Gujranwala',
    'Gujrat',
    'Hafizabad',
    'Jhang',
    'Jhelum',
    'Kamalia',
    'Kamoke',
    'Kasur',
    'Khanewal',
    'Khanpur',
    'Kharian',
    'Kotli',
    'Lahore',
    'Layyah',
    'Lodhran',
    'Mandi Bahauddin',
    'Mianwali',
    'Mirpur',
    'Multan',
    'Muzaffargarh',
    'Nankana Sahib',
    'Narowal',
    'Okara',
    'Pakpattan',
    'Rahim Yar Khan',
    'Rajanpur',
    'Rawalpindi',
    'Sahiwal',
    'Sargodha',
    'Sheikhupura',
    'Toba Tek Singh',
    'Vehari',
    'Wazirabad',
  ],
  Sindh: [
    'Badin',
    'Dadu',
    'Hyderabad',
    'Jacobabad',
    'Jamshoro',
    'Karachi',
    'Khipro',
    'Larkana',
    'Matiari',
    'Mirpur Khas',
    'Nawabshah',
    'Sanghar',
    'Shaheed Benazirabad',
    'Shikarpur',
    'Sukkur',
    'Tando Allahyar',
    'Tando Muhammad Khan',
    'Tharparkar',
    'Thatta',
    'Umerkot',
  ],
  'Khyber Pakhtunkhwa': [
    'Abbottabad',
    'Astore',
    'Bagh',
    'Bannu',
    'Battagram',
    'Buner',
    'Charsadda',
    'Chitral',
    'Dera Ismail Khan',
    'Ghanche',
    'Ghizer',
    'Gilgit',
    'Hangu',
    'Haripur',
    'Hattian Bala',
    'Hunza',
    'Karak',
    'Kohat',
    'Lakki Marwat',
    'Mansehra',
    'Mardan',
    'Mingora',
    'Muzaffarabad',
    'Neelum',
    'Nowshera',
    'Peshawar',
    'Rawalakot',
    'Shangla',
    'Skardu',
    'Swabi',
    'Swat',
    'Tank',
    'Upper Dir',
  ],
  Balochistan: [
    'Barkhan',
    'Chagai',
    'Dera Bugti',
    'Gwadar',
    'Kalat',
    'Khuzdar',
    'Lasbela',
    'Loralai',
    'Mastung',
    'Nasirabad',
    'Panjgur',
    'Quetta',
    'Turbat',
    'Zhob',
  ],
};

/** All cities (alphabetical, unique). */
export const PAKISTAN_CITIES = [
  ...new Set(PAKISTAN_PROVINCES.flatMap((p) => CITIES_BY_PROVINCE[p])),
].sort((a, b) => a.localeCompare(b)) as readonly string[];

export type PakistanCity = (typeof PAKISTAN_CITIES)[number];

const PROVINCE_ALIASES: Record<string, PakistanProvince> = {
  ict: 'Islamabad Capital Territory',
  islamabad: 'Islamabad Capital Territory',
  'islamabad capital territory': 'Islamabad Capital Territory',
  punjab: 'Punjab',
  sindh: 'Sindh',
  kpk: 'Khyber Pakhtunkhwa',
  kp: 'Khyber Pakhtunkhwa',
  'khyber pakhtunkhwa': 'Khyber Pakhtunkhwa',
  balochistan: 'Balochistan',
};

export function normalizeProvince(province?: string | string[]): PakistanProvince | undefined {
  if (!province) return undefined;
  const trimmed = (Array.isArray(province) ? province[0] : province).trim();
  if (!trimmed) return undefined;
  if ((PAKISTAN_PROVINCES as readonly string[]).includes(trimmed)) {
    return trimmed as PakistanProvince;
  }
  return PROVINCE_ALIASES[trimmed.toLowerCase()];
}

export function getCitiesForProvince(province?: string): readonly string[] {
  const normalized = normalizeProvince(province);
  if (!normalized) return PAKISTAN_CITIES;
  return CITIES_BY_PROVINCE[normalized];
}

/** Lowercase tokens for spellcheck (full names + significant parts). */
export function getPakistanCitySpellcheckWords(): string[] {
  const words = new Set<string>();
  for (const city of PAKISTAN_CITIES) {
    words.add(city.toLowerCase());
    city
      .toLowerCase()
      .split(/[\s/-]+/)
      .forEach((part) => {
        if (part.length > 2) words.add(part);
      });
  }
  return Array.from(words);
}

export interface CityAutocompleteMatch {
  full: string;
  suffix: string;
}

function resolveCityPool(province?: string): readonly string[] {
  return getCitiesForProvince(province);
}

/** First alphabetical city in the province whose name starts with `input`. */
export function getCityAutocomplete(
  input: string,
  province?: string,
): CityAutocompleteMatch | null {
  const query = input.trim();
  if (!query) return null;

  const lower = query.toLowerCase();
  const pool = resolveCityPool(province);
  const match = pool.find((city) => {
    const cityLower = city.toLowerCase();
    return cityLower.startsWith(lower) && cityLower !== lower;
  });

  if (!match) return null;
  return { full: match, suffix: match.slice(query.length) };
}

/** Cities matching prefix within the selected province. */
export function searchPakistanCities(
  input: string,
  province?: string,
  limit = 8,
): string[] {
  const query = input.trim().toLowerCase();
  if (!query) return [];

  return resolveCityPool(province)
    .filter((city) => city.toLowerCase().startsWith(query))
    .slice(0, limit);
}

/** True when input exactly matches a city in the province pool (case-insensitive). */
export function isExactPakistanCity(input: string, province?: string): boolean {
  const query = input.trim().toLowerCase();
  if (!query) return false;
  return resolveCityPool(province).some((city) => city.toLowerCase() === query);
}
